import {formatCopy} from 'utils/typography';
import { centerDemoField } from 'components/cabinet/demo/center-field';
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import { useTranslation } from "react-i18next";
import {useFormContext} from "react-hook-form";
import {useDispatch} from "react-redux";
import debounce from "lodash/debounce";
import isArray from 'lodash/isArray'

import TextField from "@mui/material/TextField";
import {InputAdornment} from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import Paper from "@mui/material/Paper";
import Typography from '@mui/material/Typography';
import {Close, LocationOn} from '@vedmarket/ui-kit';

import {COLORS} from "../../../../../../../../../../../../../config/constants";
import {useLazyGetTnvedQuery} from "../../../../../../../../../../../../../api/tnved";
import {useInvoices, useUpdateProductById} from "../../../../../../../../../../../../../api/calc";
import {
  useLazyGetCountryQuery,
  useLazyGetDeclarationsQuery
} from "../../../../../../../../../../../../../api/dictionary";
import { getProductById } from "../../../../../../../../helpers";
import { isVehicleHsCode, PACKAGE_FORM, PackageForm } from '../add_form/helpers';
import Excise from '../excise';
import {getCountries} from "./helpers";
import ModalCode from "../../../../../../../../../customs/components/modal_code";
import HelpTabs from "../help_tabs";
import {useDemoProductCountry} from 'components/cabinet/demo/steps/packaging/use-product-form';
import './row-code.scss';
import {LinkPopover} from '@vedmarket/ui-kit';

type OptionData = {
  code: string
  label: string
  id: string
}

type Props = {
  defaultCountry?: OptionData
  isAdditional: boolean
  id?: number
  invoiceId?: number
  insideModal?: boolean
  addProductRef: React.MutableRefObject<HTMLInputElement | null>;
  setAdditional(value: boolean): void
}

const RowCode = ( { defaultCountry, setAdditional, isAdditional, id, invoiceId, insideModal, addProductRef } : Props) => {

  const { t } = useTranslation()

  const { register, formState, clearErrors, setError, setValue, trigger, getValues } = useFormContext<PackageForm>()

  const [ openModalCode, setOpenModalCode ] = useState(false);

  const [ codeToCheck, setCodeToCheck ] = useState('');

  const [ tnvedInfo, setTnvedInfo ] = useState<TnvedInfo>();

  const { errors } = formState;
  const codeHelpRef = useRef<HTMLButtonElement>(null);
  const [ codeHelpSpacing, setCodeHelpSpacing ] = useState(3);
  const codeErrorMessage = errors[PACKAGE_FORM.CODE]?.message;

  useLayoutEffect(() => {
    const helper = codeHelpRef.current?.previousElementSibling?.querySelector<HTMLElement>('.MuiFormHelperText-root.Mui-error');

    if (!helper || !codeErrorMessage) {
      setCodeHelpSpacing(3);
      return;
    }

    const updateSpacing = () => {
      const margin = parseFloat(getComputedStyle(helper).marginTop) || 0;
      setCodeHelpSpacing(Math.ceil(helper.offsetHeight + margin + 8));
    };
    const observer = new ResizeObserver(updateSpacing);

    observer.observe(helper);
    updateSpacing();

    return () => observer.disconnect();
  }, [ codeErrorMessage ]);


  const [ countriesRequest, { data, isLoading: isLoadingCountry, isSuccess: isSuccessCountries } ] = useLazyGetCountryQuery()

  const [ tnvedRequest, { error: errorCode } ] = useLazyGetTnvedQuery()

  const [ declarationsRequest ] = useLazyGetDeclarationsQuery()

  const [ exciselist, setExciselist ] = useState<Excise[] | undefined>();

  const [ currentCode, setCurrentCode ] = useState("");

  const [ helpIsOpen, setHelpIsOpen ] = useState(false);

  const [ isExpandedGuideTab, setIsExpandedGuideTab ] = useState(true);
  const [ isExpandedDeclarationTab, setIsExpandedDeclarationTab ] = useState(true);

  const [ defaultValue ] = useState<OptionData | undefined>(defaultCountry)

  const [ userHasStartedTyping, setUserHasStartedTyping ] = useState(false);


  const invoices = useInvoices();

  const dispatch = useDispatch()

  const debouncedRequest = useRef(debounce(countriesRequest, 1000)).current

  useDemoProductCountry({
    onSearch: query => countriesRequest({search: query}),
    onSearchStart: () => setUserHasStartedTyping(true),
  })

  useEffect(() => {
    if (errorCode) {
      setError(PACKAGE_FORM.CODE, { type: "custom",  message: t('errors__message__incorrectHsCode') });
    }
  }, [ errorCode ])

  useEffect(() => {
    const code = getValues(PACKAGE_FORM.CODE);
    if (code) {
      handleCheckCode(code, true);
    }
  }, [ getValues(PACKAGE_FORM.CODE) ])

  const handleOpenModalCode = (value: string) => {
    setCodeToCheck(value);
    tnvedRequest(value)?.then((response) => {
      setTnvedInfo(response?.data);
      setOpenModalCode(true);
    });
  }

  const handleCheckCode = (value: string, isFirstTime?: boolean, shouldSendDeclarationRequest?: boolean) => {

    const newValue = value.replace(/\s/g, '');
    const currentFormCode = getValues(PACKAGE_FORM.CODE)?.replace(/\s/g, '');

    if (newValue === currentCode && currentFormCode === newValue) {
      return;
    }

    setValue(PACKAGE_FORM.CODE, newValue, {
      shouldDirty: true
    });

    if (!newValue) {
      setCurrentCode('');
    }

    if (newValue) {
      setCurrentCode(newValue);
      tnvedRequest(newValue).then(response => {

        // @ts-ignore
        if (response.data?.Error) {
        // @ts-ignore
          setError(PACKAGE_FORM.CODE, { type: "custom",  message: t("errors__message__incorrectHsCode")});
        } else if (response.data?.GoodInfo){
          clearErrors(PACKAGE_FORM.CODE);
          // if (shouldSendDeclarationRequest) {
          //   declarationsRequest({ code: value });
          // }
        }

        const excise = response?.data?.GoodInfo?.Exciselist?.Excise;

        const hasEmptyExcise = !isArray(excise) && excise?.Value === "0";

        // Для авто (8703/8704) блок доп. полей нужен всегда — там параметры ТС
        const isVehicle = isVehicleHsCode(newValue);

        // @ts-ignore
        setAdditional(hasEmptyExcise && !isVehicle ? false : Boolean(excise) || isVehicle)
        if (hasEmptyExcise && !isVehicle) { return }

        excise && setExciselist(isArray(excise) ? excise : [ excise ] );
        if (excise && !isFirstTime) {

          const product = getProductById(invoices, invoiceId, id);

          const data = {
            ...product,
            [PACKAGE_FORM.EXCISE_VALUE]: "",
            [PACKAGE_FORM.EXCISE_RATE]: {}
          };

          // @ts-ignore
          dispatch(useUpdateProductById(data));
          setValue(PACKAGE_FORM.EXCISE_VALUE, undefined, {
            shouldDirty: true
          });
          setValue(PACKAGE_FORM.EXCISE_RATE, undefined, {
            shouldDirty: true
          });
          trigger(PACKAGE_FORM.PALLETES)
        }
      });
    }
    return trigger(PACKAGE_FORM.PALLETES)
  }

  const handleCheckCountryCode = (value: string) => {

    value !== '' ? clearErrors(PACKAGE_FORM.COUNTRY_CODE) : setError(PACKAGE_FORM.COUNTRY_CODE, { type: "custom",  message: t('common__text__isRequired') })
    return trigger(PACKAGE_FORM.PALLETES)
  }

  const handleOpenHelp = () => {
    setHelpIsOpen(!helpIsOpen);
  }

  const handleApplyCode = (code?: string) => {
    if (code) {
      setIsExpandedGuideTab(false);
      setIsExpandedDeclarationTab(true);
    }

    handleScrollToCode();
    handleCheckCode(code? code: codeToCheck);
  }

  const handleScrollToCode = () => {
    const productElement = addProductRef.current;
    if (productElement && !centerDemoField(productElement)) {
      productElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  return <>
    <div className='add-product-code'>
      <div className='product-field'>
        <Autocomplete
          className={`add-product__${PACKAGE_FORM.COUNTRY_CODE}`}
          options={getCountries(data)}
          loading={isLoadingCountry}
          noOptionsText={t('calc__text__main__noOptionsFound') }
          {...register(PACKAGE_FORM.COUNTRY_CODE, { required: true })}
          defaultValue={defaultValue}
          forcePopupIcon={false}
          // @ts-ignore
          onChange={(_, value: { code: string, label: string } | null ) => {
            if (!value) return
            setValue(PACKAGE_FORM.COUNTRY_CODE, value.code, {
              shouldDirty: true
            })
            return handleCheckCountryCode(value.label)
          }}
          filterOptions={(options) => options}
          PaperComponent={({ children }) => {
            return (
              <Paper>
                {userHasStartedTyping && isSuccessCountries && children}
              </Paper>
            )
          }}
          isOptionEqualToValue={(o: OptionData, v: OptionData) => o.code === v.code}
          loadingText={t('common__message__loadingText')}
          renderInput={(params) => (
            <TextField
              {...params}
              onChange={({ target: { value: query } }) => {
                if (query.length >= 3) {
                  debouncedRequest({search: query});
                  setUserHasStartedTyping(true);
                }
              }}
              error={Boolean(errors[PACKAGE_FORM.COUNTRY_CODE])}
              variant='outlined'
              label={t('print__text__countryOfOriginLabel')}
              // disabled={isLoading}
              InputProps={{
                ...params.InputProps,
                startAdornment: <InputAdornment position='start' sx={{marginRight: '8px'}}>
                  <LocationOn style={{
                    width: '24px',
                    height: '24px',
                    color: COLORS.LIGHT_ACTION,
                    fill: 'currentColor',
                  }} />
                </InputAdornment>,
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root.MuiInputBase-adornedStart': {
                  minHeight: '48px',
                  padding: '12px',
                },
                '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
                  padding: 0,
                  lineHeight: '24px',
                  letterSpacing: 0,
                },
              }}
            />)}/>
        <LinkPopover
          linkText={t('calc__help__productCountryLink')}
          text={formatCopy(t('calc__help__productCountry'))}
        />
      </div>
      <div className={`add-product__${PACKAGE_FORM.CODE}`}>
        <TextField
          {...register(PACKAGE_FORM.CODE, { required: true, minLength: { value: 10, message: t('common__text__codeOfHS') }, maxLength: { value: 10, message: t('common__text__codeOfHS') } })}
          error={Boolean(errors[PACKAGE_FORM.CODE])}
          helperText={errors[PACKAGE_FORM.CODE]?.message}
          variant='outlined'
          onBlur={({ target: { value } }) => {
            handleCheckCode(value, false, true);
          }}
          onChange={({ target: { value } }) => {
            const newValue = value.replace(/\s./g, '')
            if (newValue.length >= 10) handleCheckCode(newValue, false, true);
            return newValue
          }}
          label={t('common__text__codeOfHS')}
          // disabled={isLoading}
          InputLabelProps={{ shrink: true }}
        />
        <Typography
          component='button'
          type='button'
          variant='caption'
          ref={codeHelpRef}
          className='product-code-help'
          aria-expanded={helpIsOpen}
          onClick={handleOpenHelp}
          sx={{
            color: COLORS.BLUE,
            fontSize: '12px',
            lineHeight: '18px',
            letterSpacing: 0,
            margin: `${codeHelpSpacing}px 12px 0`,
          }}
        >
          {t('common__text__help')}
          {helpIsOpen && <Close style={{
            width: '16px',
            height: '16px',
            fill: 'currentColor',
          }} />}
        </Typography>
      </div>
      {helpIsOpen && <HelpTabs
        handleApplyCode={handleApplyCode}
        isExpandedGuideTab={isExpandedGuideTab}
        isExpandedDeclarationTab={isExpandedDeclarationTab}
        code={getValues(PACKAGE_FORM.CODE)}
        handleOpenModalCode={handleOpenModalCode}
      />}
    </div>
    {isAdditional && <Excise data={exciselist} id={id} invoiceId={invoiceId} insideModal={insideModal} />}
    <ModalCode
      isOpen={openModalCode}
      onClose={() => setOpenModalCode(false)}
      code={codeToCheck}
      tnvedInfo={tnvedInfo}
      isHelp
      handleApplyCode={handleApplyCode}
    />
  </>
}

export default RowCode
